import { ActionTree, Module } from "vuex";
import { RootState } from "../store";
import { pulsingDot } from "../../../../utils/helpers/pulsing_point_mapbox";
import graph_client from "../../../../utils/graphql/graph_client";
import { GetLiveLocatorSessionByObfuscatedId } from "../../../../utils/graphql/live-locator-graph";
import { MeasurementPrefUtility } from "../../../../utils/helpers/MeasurementPref";
import { database, ref, off, onValue} from '../../../../utils/firebase/database'
import mapboxgl from "mapbox-gl";
import { MapLayerCategory } from "../../../../utils/layer-registry";
import { lastCoordinate, buildDotPointFeatureCollection } from "../../liveLocatorMap";

export class LiveLocatorState {
  miniCoordsBar: string = ''
  geoJsonFeature: any = null
  liveLocatorData: any = null
  distance: string = ''
  elevationGain: string = ''
  loading: boolean = false
  initialBoundsSet: boolean = false

  dataLoaded: boolean = false
}

let listeners: any[] = []

const actions: ActionTree<LiveLocatorState, RootState> = {
  watchFirebase(store) {
    store.state.loading = false
    const databaseReference = ref(database, 'live_locator_sessions/' + window.obfuscatedId)
    listeners.forEach((listener) => {
      off(databaseReference, 'value', listener)
    })
    listeners.push(
      onValue(databaseReference, (snapshot) => {
        store.state.dataLoaded = true
        if (snapshot.exists()) {
          let geoJson = snapshot.val()
          if (geoJson != null && geoJson.geometry != null && geoJson.geometry.coordinates.length > 1) {
            store.state.geoJsonFeature = geoJson
            store.dispatch('lineStringUpdated')
            console.log("Live locator data")
          } else {
            console.log('No data found')
          }
        } else {
          if (store.state.liveLocatorData) {
            store.state.liveLocatorData.endTime = new Date().toISOString()
          }
          console.log('No data found')
        }
      })
    )
  },
  lineStringUpdated(store) {
    const geoJson = store.state.geoJsonFeature
    let measurementPref = MeasurementPrefUtility.preference()
    store.state.elevationGain = measurementPref.smallUnitFormatter(
      geoJson.properties.elevation_gain_meters, false
    )
    store.state.distance = measurementPref.smallUnitFormatter(
      geoJson.properties.distance_meters, true
    )

    const lastPoint = lastCoordinate(geoJson)
    if (lastPoint && !store.state.initialBoundsSet) {
      window.map.flyTo({ center: lastPoint, zoom: 17 })
    }
    store.state.initialBoundsSet = true

    store.dispatch('drawLine')
  },
  // Idempotent: (re)adds the track line + pulsing "current position" dot from
  // the current geoJsonFeature and refreshes their data. Routing every layer
  // through window.layerRegistry (instead of window.map directly) is what makes
  // the line survive a base-map style change — a raw setStyle() wipes all custom
  // sources/layers, so this must be re-dispatched on every style (re)load.
  drawLine(store) {
    const geoJson = store.state.geoJsonFeature
    if (!geoJson) return
    const lastPoint = lastCoordinate(geoJson)
    if (!lastPoint) return

    const map = window.map
    const registry = window.layerRegistry
    if (!map || !registry) return

    // --- track line ---
    registry.addSource('line', {
      type: 'geojson',
      // @ts-ignore — geoJson is a Feature, accepted as GeoJSON source data
      data: geoJson,
    })
    registry.addLayer(
      {
        id: 'line',
        type: 'line',
        source: 'line',
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-color': '#DB2B35',
          'line-width': 5,
        },
      },
      MapLayerCategory.Annotations,
      { z: 9 }
    )
    const existingLine = map.getSource('line') as mapboxgl.GeoJSONSource | undefined
    // @ts-ignore — geoJson Feature is valid GeoJSON input for setData
    if (existingLine) existingLine.setData(geoJson)

    // --- pulsing "current position" dot ---
    if (!map.hasImage('pulsing-dot')) {
      map.addImage('pulsing-dot', pulsingDot, { pixelRatio: 2 })
    }
    const dotData = buildDotPointFeatureCollection(lastPoint)
    registry.addSource('dot-point', {
      type: 'geojson',
      // @ts-ignore
      data: dotData,
    })
    registry.addLayer(
      {
        id: 'layer-with-pulsing-dot',
        type: 'symbol',
        source: 'dot-point',
        layout: {
          'icon-image': 'pulsing-dot',
        },
      },
      MapLayerCategory.Top,
      { z: 10 }
    )
    const existingDot = map.getSource('dot-point') as mapboxgl.GeoJSONSource | undefined
    // @ts-ignore
    if (existingDot) existingDot.setData(dotData)
  },
  async getLiveLocatorData(store) {
    try {
      store.state.liveLocatorData = (await graph_client.request(
        GetLiveLocatorSessionByObfuscatedId, { obfuscatedId: window.obfuscatedId }
      )).liveLocatorSessionByObfuscatedId
    } catch (e) {
      alert('Error getting live locator data: ' + e)
    }
  }
}

const mutations = {
  setMiniCoordsBar(state: LiveLocatorState, miniCoordsBar: string) {
    state.miniCoordsBar = miniCoordsBar
  }
}

const getters = {
  mapOptions(state: LiveLocatorState): object {
    return {
      container: 'map', // container ID
      zoom: 5, // starting zoom
      maxPitch: 85, // limit the pitch to 85 degrees
    }
  }
}

const state = new LiveLocatorState();

export default class LiveLocatorModule implements Module<LiveLocatorState, RootState> {
  namespaced = true
  state = state
  actions = actions
  mutations = mutations
  getters = getters
}
